import { Icon } from '@common/index';
import * as Popover from '@radix-ui/react-popover';
import React, {
    useEffect,
    useRef,
    useState
} from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import styles from './index.module.scss';

interface MetricTooltipProps {
    title?: string;
    formula?: string;
    detail?: string;
    sql?: string;
    children?: React.ReactNode;
}

const MetricTooltip: React.FC<MetricTooltipProps> = ({
    title,
    formula,
    detail,
    sql,
    children
}) => {
    const [open, setOpen] = useState(false);
    const [sqlExpanded, setSqlExpanded] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setOpen(false);
        }, 150);
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <div 
                    className={styles.trigger}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {children}
                    <div className={styles.infoIcon}>
                        <Icon name="info" size={16} color="#999" />
                    </div>
                </div>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    className={styles.content}
                    sideOffset={5}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    side="top"
                    align="center"
                    avoidCollisions={true}
                    collisionPadding={{
                        top: sql ? 300 : 20,
                        bottom: 20,
                    }}
                >
                    <Popover.Arrow className={styles.arrow} width={16} height={8} />
                    
                    {title ? <div className={styles.title}>{title}</div> : null}

                    {formula ? (
                        <div className={styles.formula}>
                            {formula}
                        </div>
                    ) : null}

                    {detail ? (
                        <div className={styles.detailSection}>
                            <p className={styles.detailLabel}>DETAIL</p>
                            <div className={styles.detailItem}>
                                <div className={styles.dot} />
                                <p className={styles.detailText}>{detail}</p>
                            </div>
                        </div>
                    ) : null}

                    {sql ? (
                        <div className={styles.sqlContainer}>
                            <div
                                className={styles.sqlHeader}
                                onClick={() => setSqlExpanded(!sqlExpanded)}
                            >
                                <span className={styles.sqlLabel}>QUERY</span>
                                <svg
                                    width={14}
                                    height={14}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className={styles.sqlChevron}
                                    style={{
                                        transform: sqlExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s'
                                    }}
                                >
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                            </div>
                            {sqlExpanded ? (
                                <div className={styles.sqlContent}>
                                    <SyntaxHighlighter
                                        language="sql"
                                        style={oneLight}
                                        customStyle={{
                                            margin: 0,
                                            padding: 0,
                                            background: 'transparent',
                                            fontSize: '12px',
                                            fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace",
                                            width: '100%',
                                            overflow: 'hidden',
                                        }}
                                        codeTagProps={{
                                            style: {
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                fontFamily: 'inherit',
                                            }
                                        }}
                                    >
                                        {sql}
                                    </SyntaxHighlighter>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};

export default MetricTooltip;
